import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material'
import { type FormEvent, useState } from 'react'
import { useNotify, useTranslate } from 'react-admin'
import { authErrorCode, isWrongCode, nhost } from '~/helpers'
import { groupKey } from './twoFactor'

/**
 * Turning two-factor sign-in on: nhost/auth makes a secret, shown as a QR code
 * and as text; the first code from the app proves it was taken over, and only
 * then is it switched on.
 */
export const TwoFactorSetup = ({ onDone }: { onDone: () => void }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const [secret, setSecret] = useState<{ imageUrl: string; totpSecret: string } | null>(null)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const start = async () => {
    setBusy(true)
    setError(null)
    try {
      const { body } = await nhost.auth.changeUserMfa()
      setSecret(body)
    } catch (e: any) {
      setError(authErrorCode(e) ?? e.message)
    } finally {
      setBusy(false)
    }
  }

  const confirm = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await nhost.auth.verifyChangeUserMfa({ code: code.trim(), activeMfaType: 'totp' })
      notify('account.mfa.activated', { type: 'success' })
      setSecret(null)
      setCode('')
      onDone()
    } catch (err: any) {
      setError(
        isWrongCode(err) ? translate('account.mfa.invalid') : (authErrorCode(err) ?? err.message)
      )
    } finally {
      setBusy(false)
    }
  }

  if (!secret) {
    return (
      <>
        {error && (
          <Alert severity='error' sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        )}
        <Button variant='contained' onClick={start} disabled={busy} data-testid='mfa-setup'>
          {busy && <CircularProgress size={18} sx={{ mr: 1 }} />}
          {translate('account.mfa.setup')}
        </Button>
      </>
    )
  }

  return (
    <Box component='form' onSubmit={confirm} autoComplete='off'>
      <Typography variant='body2' sx={{ mb: 1.5 }}>
        {translate('account.mfa.scan')}
      </Typography>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ alignItems: 'center', mb: 2 }}
      >
        <Box
          component='img'
          src={secret.imageUrl}
          alt={translate('account.mfa.qr')}
          sx={{ width: 180, height: 180, imageRendering: 'pixelated', bgcolor: '#fff', p: 1 }}
        />
        <Box
          component='code'
          data-testid='mfa-secret'
          sx={{
            fontSize: 16,
            letterSpacing: 1,
            bgcolor: 'action.hover',
            px: 1,
            py: 0.5,
            borderRadius: 0.5,
          }}
        >
          {groupKey(secret.totpSecret)}
        </Box>
      </Stack>
      {error && (
        <Alert severity='error' sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}
      <Stack direction='row' spacing={1} sx={{ alignItems: 'flex-start' }}>
        <TextField
          size='small'
          label={translate('account.mfa.code')}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          slotProps={{
            htmlInput: {
              inputMode: 'numeric',
              autoComplete: 'one-time-code',
              'data-testid': 'mfa-code',
            },
          }}
          autoFocus
        />
        <Button type='submit' variant='contained' disabled={busy || code.length !== 6}>
          {translate('account.mfa.activate')}
        </Button>
        <Button onClick={() => setSecret(null)} disabled={busy}>
          {translate('ra.action.cancel')}
        </Button>
      </Stack>
    </Box>
  )
}

export default TwoFactorSetup
